import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface InboxItem {
  readonly assignmentId: string;
  readonly jobRequestId: string;
  readonly jobCode: string;
  readonly status: string;
  readonly priority: string;
  readonly territoryId: string;
  readonly acceptanceDueAt: string;
}

const STATUSES = ['ALL', 'PENDING', 'ACCEPTED', 'REJECTED', 'CLARIFY'] as const;

export function VendorInboxPage(): ReactNode {
  const { token } = useAuth();
  const [items, setItems] = useState<readonly InboxItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('ALL');

  useEffect(() => {
    let alive = true;
    const qs = status === 'ALL' ? '' : `?status=${status}`;
    apiRequest<{ items: readonly InboxItem[] }>(`/v1/vendor/inbox${qs}`, { token })
      .then((r) => { if (alive) setItems(r.items); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, status]);

  if (error) return <main aria-label="vendor-inbox"><p role="alert">{error}</p></main>;
  if (!items) return <main aria-label="vendor-inbox"><p>Loading…</p></main>;

  return (
    <main aria-label="vendor-inbox">
      <h1>Vendor · Inbox</h1>
      <label htmlFor="status-filter">Status</label>
      <select id="status-filter" aria-label="status-filter" value={status} onChange={(e) => setStatus(e.target.value as (typeof STATUSES)[number])}>
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {items.length === 0 ? (
        <p aria-label="empty">No assignments</p>
      ) : (
        <table aria-label="inbox-table">
          <thead><tr><th>Job</th><th>Status</th><th>Priority</th><th>Territory</th><th>Due</th><th></th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.assignmentId} aria-label={`row-${it.assignmentId}`}>
                <td>{it.jobCode}</td>
                <td>{it.status}</td>
                <td>{it.priority}</td>
                <td>{it.territoryId}</td>
                <td>{it.acceptanceDueAt}</td>
                <td><Link to={`/vendor/portal/assignment/${it.assignmentId}`} aria-label={`open-${it.assignmentId}`}>Open</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
